import React from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import {
  BoltIcon,
  MapIcon,
  Cog6ToothIcon,
  BeakerIcon,
} from '@heroicons/react/24/outline';
import { zoneBadge, zoneIsActive } from '../../utils/liveBoatUtils';

const Bar = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 0.5rem;
  overflow-x: auto;
  padding: 0.25rem 0 0.5rem;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    height: 4px;
  }
`;

const Tab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
  padding: 0.65rem 1rem;
  border-radius: ${theme.borderRadius.md};
  border: 1px solid
    ${(p) => (p.$selected ? theme.colors.secondary.main : theme.colors.border.primary)};
  background: ${(p) =>
    p.$selected ? 'rgba(14, 165, 233, 0.12)' : theme.colors.background.paper};
  color: ${(p) => (p.$selected ? theme.colors.secondary.light : theme.colors.text.primary)};
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    border-color: ${theme.colors.secondary.main};
  }
`;

const TabText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.1rem;
`;

const TabLabel = styled.span`
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.semibold};
`;

const TabMetric = styled.span`
  font-size: ${theme.fontSizes.xs};
  color: ${(p) => (p.$selected ? 'rgba(186, 230, 253, 0.9)' : theme.colors.text.secondary)};
`;

const LiveDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${theme.colors.success.main};
  flex-shrink: 0;
  opacity: ${(p) => (p.$on ? 1 : 0)};
`;

const ICONS = {
  bolt: BoltIcon,
  compass: MapIcon,
  engine: Cog6ToothIcon,
  tank: BeakerIcon,
};

const SystemTabs = ({ zones, roles, houseSoc, engineAlarms, selectedZoneId, onSelectZone }) => {
  return (
    <Bar role="tablist" aria-label="Boat systems">
      {zones.map((zone) => {
        const Icon = ICONS[zone.icon] || BoltIcon;
        const selected = selectedZoneId === zone.id;
        const active = zoneIsActive(zone, roles);
        const badge = zoneBadge(zone, roles, houseSoc, engineAlarms);

        return (
          <Tab
            key={zone.id}
            type="button"
            role="tab"
            aria-selected={selected}
            $selected={selected}
            onClick={() => onSelectZone(zone.id)}
          >
            <LiveDot $on={active} aria-hidden />
            <Icon className="w-5 h-5" style={{ flexShrink: 0, opacity: 0.9 }} />
            <TabText>
              <TabLabel>{zone.label}</TabLabel>
              <TabMetric $selected={selected}>{badge}</TabMetric>
            </TabText>
          </Tab>
        );
      })}
    </Bar>
  );
};

export default SystemTabs;
